import React from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@lightspeed/flame/Button';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';
import capitalize from 'lodash/capitalize';

import { toBoolean } from 'pages/utils/sanitize';
import { PrinterImage } from 'pages/views/devices/components/PrinterImage';
import { PRINTER_CONNECTIONS } from 'root/apps/pages/views/devices/types';

import { StyledModal, StyledRadio } from './Print.style';

export enum BypassOptions {
  HUB = 'HUB',
  BROWSER = 'BROWSER',
}

const PrintBypass: React.FC<MerchantosPrintParams | MerchantosIframePrintParams> = (props) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const printer = useSelector((state) => state.hub.defaultPrinters.RECEIPT);
  const [option, setOption] = React.useState(
    window.localStorage.getItem('HUB_BYPASS') as BypassOptions | null
  );
  const [asInvoice, setAsInvoice] = React.useState<boolean>(
    toBoolean(window.localStorage.getItem('HUB_BYPASS_AS_INVOICE'))
  );
  const isSaleReceipt =
    'url' in props ? props.url.match(/DisplayTemplate\/Sale\/\d+\.html/) : false;
  const isGiftReceipt = 'url' in props ? props.url.includes('gift_receipt=1') : false;

  // @NOTE: Allow invoice once the option is available on the ReceiptEditor
  const isInvoiceAllowed =
    isGiftReceipt && window.merchantos.options.itemizedGiftReceipt ? false : isSaleReceipt;

  const handleDismiss = () => {
    setIsOpen(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOption(event.target.value as BypassOptions);
  };

  const handleAsInvoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAsInvoice(event.target.checked);
  };

  const handlePrint = () => {
    window.localStorage.setItem('HUB_BYPASS_AS_INVOICE', `${asInvoice}`);
    window.localStorage.setItem('HUB_BYPASS', option!);
    setIsOpen(false);

    // @NOTE: Make sure the bypass modal closes before the browser print dialog is prompted
    setTimeout(() => {
      if ('id' in props) {
        window.merchantos.print.hubPrintByFrameId({
          ...props,
          bypass: option!,
        });
      } else {
        window.merchantos.print.printUrl({
          ...props,
          bypass: option!,
          ...(isSaleReceipt && asInvoice && option === BypassOptions.BROWSER
            ? { url: `${props.url}&print_layout=true&invoice_as_title=true` }
            : null),
        });
      }
    });
  };

  return printer ? (
    <StyledModal data-test="print-modal" isOpen={isOpen} onRequestClose={() => handleDismiss()}>
      <ModalHeader data-test="print-modal-header">Select printer</ModalHeader>
      <ModalBody>
        <Flex alignItems="center" mt={3}>
          <Box>
            <label htmlFor={`printer-bypass-${BypassOptions.HUB}`} style={{ cursor: 'pointer' }}>
              <Flex alignItems="center">
                <StyledRadio
                  id={`printer-bypass-${BypassOptions.HUB}`}
                  data-test={`printer-bypass-${BypassOptions.HUB}`}
                  name="printer-bypass"
                  checked={option === BypassOptions.HUB}
                  onChange={handleChange}
                  value={BypassOptions.HUB}
                />

                <PrinterImage model={printer.model} connected />
              </Flex>
            </label>
          </Box>
          <Box ml={3}>
            <Text mr={2} mb={1} fontSize="1rem" color="gray-850" fontWeight="bold">
              Receipt printer
            </Text>
            <Text mb={1} color="gray" fontSize="small" fontWeight="normal">
              <var>
                {capitalize(printer.brand)} {printer.model}
              </var>{' '}
              using{' '}
              {printer.connection === PRINTER_CONNECTIONS.NETWORK
                ? capitalize(printer.connection)
                : printer.connection}
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="center" mt={3}>
          <Box>
            <label
              htmlFor={`printer-bypass-${BypassOptions.BROWSER}`}
              style={{ cursor: 'pointer' }}
            >
              <Flex alignItems="center">
                <StyledRadio
                  id={`printer-bypass-${BypassOptions.BROWSER}`}
                  data-test={`printer-bypass-${BypassOptions.BROWSER}`}
                  name="printer-bypass"
                  checked={option === BypassOptions.BROWSER}
                  onChange={handleChange}
                  value={BypassOptions.BROWSER}
                />
                <PrinterImage Icon={IconPrinters} connected />
              </Flex>
            </label>
          </Box>
          <Box ml={3}>
            <Text mr={2} mb={1} fontSize="1rem" color="gray-850" fontWeight="bold">
              Other printer
            </Text>
            <Text mb={1} color="gray" fontSize="small" fontWeight="normal">
              Open the browser print dialog
            </Text>
            {isInvoiceAllowed ? (
              <Checkbox
                checked={asInvoice}
                disabled={option !== BypassOptions.BROWSER}
                onChange={handleAsInvoiceChange}
                label="Print as invoice"
              />
            ) : null}
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end" alignItems="center">
          <Button onClick={handleDismiss}>Cancel</Button>

          <Button
            onClick={handlePrint}
            variant="secondary"
            fill
            disabled={!option}
            ml={2}
            data-test="printer-bypass-print"
          >
            Print
          </Button>
        </Flex>
      </ModalFooter>
    </StyledModal>
  ) : null;
};

export default PrintBypass;
